<template>
  <div class="input-wrapper">
    <div class="relative" :class="!hasFloatLabel && 'flex flex-col-reverse gap-2'">
      <Label
        v-if="label"
        :for="name"
        :class="[
          'pointer-events-none bg-background px-2 text-sm',
          error && '!text-destructive',
          hasFloatLabel &&
            'absolute start-1 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform text-black/60 duration-150 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:bg-background/100 peer-focus:px-2 peer-focus:!text-black/100 rtl:peer-focus:left-auto rtl:peer-focus:translate-x-1/4',
        ]"
        >{{ label }}</Label
      >
      <Popover v-model:open="isOpen">
        <PopoverTrigger as-child>
          <Button
            :id="name"
            v-bind="$attrs"
            :name="name"
            variant="outline"
            :class="[
              'flex h-10 w-full justify-start rounded-md border border-input/20 bg-background/40 px-3 py-1 text-left text-sm font-normal shadow-sm ring-0 transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium hover:bg-background/100 hover:text-black/60 focus:border-input/100 focus:outline-none focus:ring-0 focus:ring-transparent disabled:cursor-not-allowed disabled:opacity-50',
              !modelValue && 'text-muted-foreground',
              error && 'border-destructive',
            ]"
          >
            <CalendarIcon class="mr-2 h-4 w-4" color="grey" />
            {{ displayDate }}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          class="w-auto rounded-md border border-input/20 bg-background p-0 shadow-sm transition-colors"
        >
          <DatePicker
            v-model="datePickerValue"
            @dateSelected="isOpen = false"
            :minYearsAgo="minYearsAgo"
          />
        </PopoverContent>
      </Popover>
    </div>
    <span class="input-error" v-if="error">{{ error }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { CalendarIcon } from 'lucide-vue-next';
import { CalendarDate } from '@internationalized/date';
import type { DateValue } from 'reka-ui';
import Button from '@ui/button/Button.vue';
import Popover from '@ui/popover/Popover.vue';
import PopoverContent from '@ui/popover/PopoverContent.vue';
import PopoverTrigger from '@ui/popover/PopoverTrigger.vue';
import Label from '@ui/label/Label.vue';
import DatePicker from './DatePicker.vue';
import moment from 'moment';

interface DatePickerProps {
  name: string;
  label?: string;
  placeholder?: string;
  error?: string;
  mode?: 'single' | 'multiple' | 'range';
  hasFloatLabel?: boolean;
  minYearsAgo?: number;
}

const props = withDefaults(defineProps<DatePickerProps>(), {
  label: undefined,
  error: undefined,
  placeholder: 'Pick a date',
  mode: 'single',
  hasFloatLabel: true,
  minYearsAgo: undefined,
});

const isOpen = ref(false);

const displayDate = computed(() => {
  return modelValue.value ? moment.utc(modelValue.value).format('LL') : props.placeholder;
});
const datePickerValue = computed({
  get() {
    const year = modelValue.value?.split('-')[0];
    const month = modelValue.value?.split('-')[1];
    const day = modelValue.value?.split('-')[2];
    if (!year || !month || !day) return undefined;
    return new CalendarDate(Number(year), Number(month), Number(day));
  },
  set(value: DateValue) {
    modelValue.value = value.toString() || '';
  },
});

const modelValue = defineModel<string>('modelValue', {
  required: true,
});
</script>
